import API_BASE from './apiBase';

// Tokens are signed with a 30-day expiry (see backend POST /api/auth/login).
// A stale token left in localStorage after a long absence still "reads" as
// logged-in unless we check it: every 401 it triggers (including on the SSE
// stream below, whose EventSource auto-reconnects forever on error) turns
// into a silent infinite retry loop for the life of the tab. Decoding exp
// locally (no signature check needed, the server verifies that) lets every
// call site's existing `if (!getToken())` guard treat it as logged-out.
function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

export function getToken() {
  const token = localStorage.getItem('token');
  if (token && isTokenExpired(token)) {
    localStorage.removeItem('token');
    return null;
  }
  return token;
}

// Stable per-tab id (sessionStorage is per-tab, unlike localStorage) used to
// tell the realtime userdata-changed stream "this connection made the save
// that triggered you, don't echo it back to yourself".
export const CLIENT_ID = (() => {
  try {
    let id = sessionStorage.getItem('anotequest_client_id');
    if (!id) {
      id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
      sessionStorage.setItem('anotequest_client_id', id);
    }
    return id;
  } catch {
    return `${Date.now()}-${Math.random()}`;
  }
})();

export function isLoggedIn() {
  return !!getToken();
}

export function notesFromServer(serverNotes) {
  if (!Array.isArray(serverNotes)) return [];

  return serverNotes
    .map((n) => {
      const raw = typeof n?.content === 'string' ? n.content : '';
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') return parsed;
      } catch {
        // ignore
      }
      return null;
    })
    .filter(Boolean);
}

// Strip any inline base64 image data from a note before it leaves the client.
// Safety net for legacy notes that still store blobs in note.images[].data —
// they inflate the JSON payload and cause 413s at nginx and Express.
// React state is never mutated; only the serialised copy is cleaned.
export function stripBase64FromNote(note) {
  if (!note || !Array.isArray(note.images) || note.images.length === 0) return note;
  const hasBlob = note.images.some(
    (img) => typeof img?.data === 'string' && img.data.startsWith('data:')
  );
  if (!hasBlob) return note;
  return {
    ...note,
    images: note.images.map((img) =>
      typeof img?.data === 'string' && img.data.startsWith('data:')
        ? { ...img, data: null }
        : img
    ),
  };
}

export function notesToServer(notes) {
  if (!Array.isArray(notes)) return [];
  return notes
    .map((n) => {
      const clientId = String(n?.id ?? '');
      if (!clientId) return null;
      return {
        clientId,
        content: JSON.stringify(stripBase64FromNote(n)),
        updatedAt: n?.updatedAt || n?.createdAt || new Date().toISOString(),
      };
    })
    .filter(Boolean);
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn('localStorage setItem failed for', key, e?.name || e);
    return false;
  }
}

export function cacheUserDataToLocalStorage(userData) {
  if (!userData || typeof userData !== 'object') return;

  // Keep a raw copy too
  safeSet('userData', JSON.stringify(userData));

  if (typeof userData.username === 'string') safeSet('anotequest_username', userData.username);
  if (userData.stats) safeSet('anotequest_stats', JSON.stringify(userData.stats));
  if (Array.isArray(userData.folders)) safeSet('anotequest_folders', JSON.stringify(userData.folders));
  if (Array.isArray(userData.notes)) safeSet('anotequest_notes', JSON.stringify(userData.notes));
  if (Array.isArray(userData.images)) safeSet('anotequest_images', JSON.stringify(userData.images));
  if (Array.isArray(userData.note_stickers)) safeSet('anotequest_note_stickers', JSON.stringify(userData.note_stickers));
  if (Array.isArray(userData.todos)) safeSet('anotequest_todos', JSON.stringify(userData.todos));
  if (Array.isArray(userData.sources)) safeSet('anotequest_sources', JSON.stringify(userData.sources));
  if (typeof userData.theme === 'string') safeSet('theme', userData.theme);

  // Optional extra keys if present
  if (Array.isArray(userData.stickers)) safeSet('anotequest_stickers', JSON.stringify(userData.stickers));
  if (Array.isArray(userData.tables)) safeSet('anotequest_tables', JSON.stringify(userData.tables));
  if (Array.isArray(userData.drawings)) safeSet('anotequest_drawings', JSON.stringify(userData.drawings));
  if (Array.isArray(userData.trash)) safeSet('anotequest_trash', JSON.stringify(userData.trash));
}

export async function login(loginValue, password) {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ login: loginValue, password }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Login failed');
  return data;
}

export async function signup(email, name, password) {
  const res = await fetch(`${API_BASE}/api/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, name, password }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Signup failed');
  return data;
}

export async function getUserData() {
  const token = getToken();
  if (!token) throw new Error('Missing token');

  const res = await fetch(`${API_BASE}/api/userdata`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Failed to load userData');
  return { userData: data?.userData, isPremium: !!data?.isPremium };
}

export async function saveUserData(userData) {
  const token = getToken();
  if (!token) throw new Error('Missing token');

  // Strip base64 blobs from note images before sending — keeps the payload
  // small and avoids 413 errors from nginx / body-size limits.
  const sanitizedPayload = Array.isArray(userData?.notes)
    ? { ...userData, notes: userData.notes.map(stripBase64FromNote) }
    : userData;

  const res = await fetch(`${API_BASE}/api/userdata`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Client-Id': CLIENT_ID,
    },
    body: JSON.stringify({ userData: sanitizedPayload }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Failed to save userData');
  return data?.userData;
}

// Opens a realtime channel that calls onChange() whenever this account's
// data was saved from another tab/device (the save that originated on this
// tab/device is excluded via CLIENT_ID, so we don't just re-fetch our own
// write). EventSource reconnects on its own if the connection drops.
// Returns a cleanup function.
export function subscribeUserDataChanges(onChange) {
  const token = getToken();
  if (!token || typeof EventSource === 'undefined') return () => {};

  const url = `${API_BASE}/api/userdata/stream?token=${encodeURIComponent(token)}&clientId=${encodeURIComponent(CLIENT_ID)}`;
  const source = new EventSource(url);
  source.addEventListener('userdata-changed', () => onChange());
  source.onerror = () => {
    // EventSource retries on its own; nothing to do here beyond not crashing.
  };

  return () => source.close();
}

export async function syncAll(notes) {
  const token = getToken();
  if (!token) throw new Error('Missing token');

  const res = await fetch(`${API_BASE}/api/sync-all`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ notes: notesToServer(notes) }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error || 'Sync failed');
  }
}